import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantManagementApi } from '../../../api/tenantManagement';
import { validateDomain } from '../../../schemas/domain.schema';
import { useOrganizationLogo } from '../../organizations/useOrganizationLogo';
import { useOrganizationFavicon } from '../../organizations/useOrganizationFavicon';

export interface CreateWebsiteParams {
  organizationId: string;
  organizationName: string;
  domain: string;
  logoFile?: File | null;
  faviconFile?: File | null;
  existingLogoUrl?: string;
  existingFaviconUrl?: string;
  logoWidth?: number;
  logoHeight?: number;
  logoPlacement?: 'header' | 'both';
  colors: {
    primary: string;
    secondary: string;
  };
  complianceLinks?: { key: string; value: string }[];
}

export interface CreateWebsiteResult {
  success: boolean;
  logoUrl: string;
  faviconUrl: string;
  domainRegistrationResult?: any;
  tenantCreationResult?: any;
}

/**
 * Hook to create a website/tenant with proper flow:
 * 1. Validate domain format
 * 2. Register domain in Firebase Auth and Hosting (BLOCKING - fail if this fails)
 * 3. Only if register succeeds: Upload logo and favicon
 * 4. Only if register succeeds: Create tenant
 *
 * Domain uniqueness is enforced by the backend — no Firestore pre-check needed.
 */
export const useCreateWebsite = () => {
  const queryClient = useQueryClient();
  const { uploadLogo } = useOrganizationLogo();
  const { uploadFavicon } = useOrganizationFavicon();

  return useMutation({
    mutationFn: async ({
      organizationId,
      organizationName,
      domain,
      logoFile,
      faviconFile,
      existingLogoUrl = '',
      existingFaviconUrl = '',
      logoWidth = 200,
      logoHeight = 100,
      logoPlacement = 'header',
      colors,
      complianceLinks = [],
    }: CreateWebsiteParams): Promise<CreateWebsiteResult> => {
      // Step 1: Validate domain format
      const validation = validateDomain(domain);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Step 2: Register domain in Firebase Auth and Hosting FIRST
      // This is the critical step - if this fails, we don't proceed with anything else
      let domainRegistrationResult;
      try {
        domainRegistrationResult = await tenantManagementApi.registerDomain({
          organisation_id: organizationId,
          domain: domain,
        });
        console.log('Domain registration result:', domainRegistrationResult.data);
      } catch (domainError) {
        console.error('Domain registration error:', domainError);
        throw new Error(
          `Failed to register domain: ${domainError instanceof Error ? domainError.message : 'Unknown error'}`
        );
      }

      // Step 3: Upload logo and favicon (only after domain registration succeeds)
      let logoUrl = existingLogoUrl;
      let faviconUrl = existingFaviconUrl;

      if (logoFile) {
        try {
          logoUrl = await uploadLogo(logoFile, organizationId, existingLogoUrl);
          console.log('Logo uploaded:', logoUrl);
        } catch (uploadError) {
          console.error('Error uploading logo:', uploadError);
          throw new Error('Failed to upload logo');
        }
      }

      if (faviconFile) {
        try {
          faviconUrl = await uploadFavicon(faviconFile, organizationId, existingFaviconUrl);
          console.log('Favicon uploaded:', faviconUrl);
        } catch (uploadError) {
          console.error('Error uploading favicon:', uploadError);
          // Continue without favicon (not critical)
        }
      }

      // Step 4: Create tenant - only called after successful domain registration and uploads
      let tenantCreationResult;
      try {
        tenantCreationResult = await tenantManagementApi.createTenant(
          {
            id: organizationId,
            name: organizationName,
            domain: {
              primary: domain,
              aliases: [],
            },
            analytics: {},
            branding: {
              colors: {
                primary: colors.primary,
                secondary: colors.secondary,
                accent: colors.primary,
              },
              logo: {
                url: logoUrl,
                width: logoWidth,
                height: logoHeight,
                placement: logoPlacement,
              } as any,
              favicon: faviconUrl
                ? {
                    url: faviconUrl,
                    width: 32,
                    height: 32,
                  }
                : undefined,
            },
          },
          complianceLinks
        );
        console.log('Tenant created successfully');
      } catch (tenantError) {
        console.error('Tenant creation error:', tenantError);
        throw new Error(
          `Failed to create tenant: ${tenantError instanceof Error ? tenantError.message : 'Unknown error'}`
        );
      }

      // Note: Domain mapping is created by createTenant API

      return {
        success: true,
        logoUrl,
        faviconUrl,
        domainRegistrationResult: domainRegistrationResult?.data,
        tenantCreationResult: tenantCreationResult?.data,
      };
    },
    onSuccess: (_, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['tenant', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['organization', variables.organizationId] });
      queryClient.invalidateQueries({ queryKey: ['domain-mapping', variables.domain] });
    },
  });
};

/**
 * Hook to register domain only (without creating tenant)
 * Useful for scenarios where you want to register first, then do other things.
 * Domain uniqueness is enforced by the backend.
 */
export const useRegisterDomain = () => {
  return useMutation({
    mutationFn: async ({
      organizationId,
      domain,
    }: {
      organizationId: string;
      domain: string;
    }): Promise<{ success: boolean; result?: any }> => {
      // Validate domain format
      const validation = validateDomain(domain);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Register domain — backend enforces uniqueness
      const result = await tenantManagementApi.registerDomain({
        organisation_id: organizationId,
        domain: domain,
      });

      return {
        success: true,
        result: result.data,
      };
    },
  });
};
